import { AccessManager, PlayerStatus, ViewCode, VersionManager, USE_FIREBASE_EMULATORS, DEV_CAPTAIN_EMAIL } from 'shared';
import { ScoreCardBuilder } from './ScorecardBuilder';
import { DevPatch } from './DevPatch';
import { bindGuardedPress } from './GuardedPress';
import { resolveSummaryHole } from './ScoreSummaryNavigation';
const ACCESS_CODE_STORAGE_KEY = 'leaderboardnow.accessCode';
const CURRENT_PLAYER_STORAGE_KEY = 'leaderboardnow.currentPlayerId';
const CURRENT_ACCESS_MODE_STORAGE_KEY = 'leaderboardnow.accessMode';
function saveBrowserAccessSession(accessCode, accessMode) {
    localStorage.setItem(ACCESS_CODE_STORAGE_KEY, ViewCode.normalize(accessCode));
    localStorage.setItem(CURRENT_ACCESS_MODE_STORAGE_KEY, accessMode);
    if (accessMode === 'gallery') {
        localStorage.removeItem(CURRENT_PLAYER_STORAGE_KEY);
    }
}
function setConnectedPlayer(memberId) {
    localStorage.setItem(CURRENT_PLAYER_STORAGE_KEY, memberId.trim());
    localStorage.setItem(CURRENT_ACCESS_MODE_STORAGE_KEY, 'player');
}
function getStoredAccessCodeForScorecard(scorecard) {
    const storedAccessCode = ViewCode.normalize(localStorage.getItem(ACCESS_CODE_STORAGE_KEY));
    if (!storedAccessCode)
        return '';
    const roundAccess = ViewCode.validateForRound(storedAccessCode, scorecard.playerAccessCode ?? scorecard.viewCode);
    if (!roundAccess.isValidForRound)
        return '';
    if (roundAccess.viewCode !== ViewCode.toGalleryCode(scorecard.viewCode ?? scorecard.playerAccessCode)) {
        return '';
    }
    return roundAccess.normalizedInput;
}
/**
 * Phase 1: build the scorecard DOM element for a given cardId.
 * No DOM mounting here; just returns the element.
 */
const _buildScorecardElement = async function buildScorecardElement(sCardId, repo) {
    const scoreCardData = await repo.ScoreCards.get(sCardId);
    if (!scoreCardData) {
        throw new Error(`Scorecard data not found for id: ${sCardId}`);
    }
    const groupData = await repo.Groups.get(scoreCardData.group);
    if (!groupData) {
        throw new Error(`Group data not found for id: ${scoreCardData.group}`);
    }
    const group = groupData;
    const course = await repo.Courses.get(scoreCardData.courseId);
    if (!course) {
        throw new Error(`Course data not found for id: ${scoreCardData.courseId}`);
    }
    const devPatch = await DevPatch.tryConnect();
    const builder = new ScoreCardBuilder(scoreCardData, repo, devPatch);
    builder.buildScoreCard(group, course);
    const summaryNavigation = createScoreSummaryNavigation(builder);
    builder.setLocalHoleScoreSavedHandler((result) => {
        void summaryNavigation.handleLocalSave(result.editedHoleIndex, result.lastCompletedHoleIndex);
    });
    bindScoreSummaryActions(builder);
    const initialMessage = "Loading score summary...";
    void summaryNavigation
        .showInitial()
        .catch((error) => {
        console.error("[ShowScorecard] Unable to build initial score summary", error);
        showUpdateMessage("Unable to build score summary");
    });
    await renderRoundRoleControls(repo, scoreCardData);
    const unsubscribeScorecardSnapshot = repo.ScoreCards.subscribe(sCardId, async (scorecard) => {
        if (!scorecard) {
            console.warn(`[Snapshot] null received for cardId=${sCardId}`);
            return;
        }
        await builder.applyScorecardSnapshot(scorecard);
        await summaryNavigation.refresh();
        await renderRoundRoleControls(repo, scorecard);
    });
    window.unsubscribeScorecardSnapshot = unsubscribeScorecardSnapshot;
    if (!builder.element || !(builder.element instanceof HTMLElement)) {
        throw new Error('Builder did not produce an HTMLElement. Missing build/init?');
    }
    return {
        element: builder.element,
        initialMessage,
    };
};
function createScoreSummaryNavigation(builder) {
    let displayedHoleIndex = null;
    let lastKnownCompletedHoleIndex = builder.getLastCompletedHoleIndex();
    const showNoScores = () => {
        displayedHoleIndex = null;
        showUpdateMessage("No scores entered", builder);
    };
    const renderHole = async (holeIndex, openEditorIfUnscored = false) => {
        const boundedHoleIndex = Math.max(0, Math.min(holeIndex, builder.getHoleCount() - 1));
        displayedHoleIndex = boundedHoleIndex;
        const html = await builder.buildIndexPageScoreSummary(boundedHoleIndex);
        showUpdateMessage(html, builder);
        configureSummaryHoleControls(builder, boundedHoleIndex, renderHole);
        if (openEditorIfUnscored &&
            !builder.isHoleScored(boundedHoleIndex) &&
            builder.canEditScores()) {
            const result = await builder.openHoleScoreEditor(boundedHoleIndex);
            if (result?.status === "cancelled") {
                const targetHoleIndex = resolveSummaryHole({
                    type: "cancel",
                    lastCompletedHoleIndex: builder.getLastCompletedHoleIndex(),
                });
                if (targetHoleIndex === null) {
                    showNoScores();
                }
                else {
                    await renderHole(targetHoleIndex);
                }
            }
        }
    };
    return {
        showInitial: async () => {
            const lastCompletedHoleIndex = builder.getLastCompletedHoleIndex();
            lastKnownCompletedHoleIndex = lastCompletedHoleIndex;
            const targetHoleIndex = resolveSummaryHole({
                type: "initial",
                lastCompletedHoleIndex,
                canEditScores: builder.canEditScores(),
            });
            if (targetHoleIndex === null) {
                showNoScores();
                return;
            }
            await renderHole(targetHoleIndex);
        },
        refresh: async () => {
            const lastCompletedHoleIndex = builder.getLastCompletedHoleIndex();
            const targetHoleIndex = resolveSummaryHole({
                type: "snapshot",
                currentDisplayedHoleIndex: displayedHoleIndex,
                previousLastCompletedHoleIndex: lastKnownCompletedHoleIndex,
                lastCompletedHoleIndex,
                canEditScores: builder.canEditScores(),
            });
            lastKnownCompletedHoleIndex = lastCompletedHoleIndex;
            if (targetHoleIndex === null) {
                showNoScores();
                return;
            }
            await renderHole(targetHoleIndex);
        },
        handleLocalSave: async (editedHoleIndex, lastCompletedHoleIndex) => {
            // The local save has already changed round progress in the builder. Record
            // it here so the Firestore echo of this same save is treated as a refresh,
            // not as a second navigation event that could override a historical edit.
            lastKnownCompletedHoleIndex = lastCompletedHoleIndex;
            const targetHoleIndex = resolveSummaryHole({
                type: "local-save",
                editedHoleIndex,
                lastCompletedHoleIndex,
            });
            if (targetHoleIndex === null) {
                showNoScores();
                return;
            }
            await renderHole(targetHoleIndex);
        },
    };
}
function configureSummaryHoleControls(builder, holeIndex, renderHole) {
    const { scoreUpdateMessageElement } = ensureUpdateShell();
    const throughText = `THROUGH ${holeIndex + 1}`;
    // AV owns the summary markup. Find the smallest element containing its
    // THROUGH HOLE heading and enhance that element in-place.
    const candidates = Array.from(scoreUpdateMessageElement.querySelectorAll("*"));
    const throughElement = candidates.find((element) => {
        if ((element.textContent ?? "").trim().toUpperCase() !== throughText)
            return false;
        return !Array.from(element.children).some((child) => (child.textContent ?? "").trim().toUpperCase() === throughText);
    });
    if (!throughElement) {
        console.warn("[ShowScorecard] THROUGH HOLE heading not found", { throughText });
        return;
    }
    const canEdit = builder.canEditScores();
    const isScored = builder.isHoleScored(holeIndex);
    const lastCompleted = builder.getLastCompletedHoleIndex();
    const firstUnscored = Math.min(lastCompleted + 1, builder.getHoleCount() - 1);
    const canGoLeft = holeIndex > 0;
    const canGoRight = holeIndex < builder.getHoleCount() - 1 &&
        isScored &&
        holeIndex < firstUnscored;
    throughElement.textContent = "";
    throughElement.classList.add("through-hole-navigation");
    const left = document.createElement("button");
    left.type = "button";
    left.className = "through-hole-nav-button";
    left.textContent = "‹";
    left.setAttribute("aria-label", "Previous hole");
    setSummaryControlEnabled(left, canGoLeft);
    const label = document.createElement("span");
    label.className = "through-hole-navigation-label";
    label.textContent = throughText;
    const right = document.createElement("button");
    right.type = "button";
    right.className = "through-hole-nav-button";
    right.textContent = "›";
    right.setAttribute("aria-label", "Next hole");
    setSummaryControlEnabled(right, canGoRight);
    throughElement.appendChild(left);
    throughElement.appendChild(label);
    if (canEdit && isScored) {
        const edit = document.createElement("button");
        edit.type = "button";
        edit.className = "through-hole-edit-button";
        edit.innerHTML = "<span>Edit</span><span>Scores</span>";
        edit.setAttribute("aria-label", `Edit scores for hole ${holeIndex + 1}`);
        throughElement.appendChild(edit);
        bindGuardedPress(edit, () => {
            void builder.openHoleScoreEditor(holeIndex);
        });
    }
    throughElement.appendChild(right);
    if (canGoLeft) {
        bindGuardedPress(left, () => void renderHole(holeIndex - 1));
    }
    if (canGoRight) {
        const nextHoleIndex = holeIndex + 1;
        bindGuardedPress(right, () => {
            const shouldOpenEditor = !builder.isHoleScored(nextHoleIndex);
            void renderHole(nextHoleIndex, shouldOpenEditor);
        });
    }
}
function setSummaryControlEnabled(button, enabled) {
    button.disabled = !enabled;
    button.setAttribute("aria-disabled", enabled ? "false" : "true");
}
function bindScoreSummaryActions(builder) {
    const { scoreUpdateMessageElement } = ensureUpdateShell();
    scoreUpdateMessageElement.onclick = (event) => {
        const target = event.target;
        if (!(target instanceof Element))
            return;
        const actionElement = target.closest('[data-action="toggle-match-play-handicap"]');
        if (!actionElement)
            return;
        void builder.toggleMatchPlayHandicaps();
    };
}
function showUpdateMessage(messageHtml, builder) {
    const { scoreUpdateMessageElement } = ensureUpdateShell();
    scoreUpdateMessageElement.innerHTML =
        messageHtml || 'No games in play';
    if (builder) {
        configureMatchPlayHandicapControl(builder, scoreUpdateMessageElement);
    }
    // A new score update should return the user to the compact live summary.
    setScorecardVisible(false);
}
function configureMatchPlayHandicapControl(builder, container) {
    const button = container.querySelector('[data-action="toggle-match-play-handicap"]');
    if (!button)
        return;
    const access = AccessManager.fromBrowserStorage(builder.stored_scoreCard);
    const canEdit = access.canEditScores();
    button.classList.toggle('is-readonly', !canEdit);
    button.setAttribute('aria-disabled', canEdit ? 'false' : 'true');
    button.tabIndex = canEdit ? 0 : -1;
    if (!canEdit) {
        button.title = `${button.textContent?.trim() || 'Match Play handicap'} — scorekeeper only`;
    }
}
export const buildScorecardElement = _buildScorecardElement;
/**
 * Phase 2: mount a built scorecard element into the page.
 */
const _mountScorecardElement = async function mountScorecardElement(element, initialMessage) {
    const mount = () => {
        const { viewButton, scorecardMount, backButton } = ensureUpdateShell();
        scorecardMount.innerHTML = '';
        scorecardMount.appendChild(element);
        setScorecardVisible(false);
        viewButton.onclick = () => {
            setScorecardVisible(true);
        };
        backButton.onclick = () => {
            setScorecardVisible(false);
        };
        showUpdateMessage(initialMessage);
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', mount, { once: true });
    }
    else {
        mount();
    }
};
export const mountScorecardElement = _mountScorecardElement;
const _showScorecard = async function showScorecard(sCardId, repo) {
    const built = await buildScorecardElement(sCardId, repo);
    await mountScorecardElement(built.element, built.initialMessage);
};
function normalizeMemberId(id) {
    return (id ?? "").trim().toLowerCase();
}
export const showScorecard = _showScorecard;
function ensureUpdateShell() {
    let root = document.getElementById('scoreUpdateHome');
    if (!root) {
        root = document.createElement('div');
        root.id = 'scoreUpdateHome';
        root.className = 'score-update-home';
        root.innerHTML = `
      <section id="scoreUpdateCard" class="score-update-card" aria-label="Live round summary">
        <button
          id="roundMenuButton"
          class="round-menu-button"
          type="button"
          aria-label="Round options"
          aria-expanded="false"
          aria-controls="roundMenuPanel"
        >
          ☰
        </button>

        <div id="roundMenuPanel" class="round-menu-panel hidden"></div>

        <div id="scoreUpdateMessage" class="score-update-message">
          No games in play
        </div>

        <button id="viewScorecardButton" class="score-update-button" type="button">
          View Live Scorecard
        </button>
      </section>

      <section id="scorecardHost" class="scorecard-host hidden" aria-label="Full scorecard">
        <button id="backToSummaryButton" class="back-to-summary-button" type="button">
          Back to Summary
        </button>
        <div id="scorecardMount"></div>
      </section>
    `;
        document.body.appendChild(root);
    }
    const summaryCard = document.getElementById('scoreUpdateCard');
    const scoreUpdateMessageElement = document.getElementById('scoreUpdateMessage');
    const viewButton = document.getElementById('viewScorecardButton');
    const menuButton = document.getElementById('roundMenuButton');
    const menuPanel = document.getElementById('roundMenuPanel');
    const scorecardHost = document.getElementById('scorecardHost');
    const scorecardMount = document.getElementById('scorecardMount');
    const backButton = document.getElementById('backToSummaryButton');
    if (!summaryCard ||
        !scoreUpdateMessageElement ||
        !viewButton ||
        !menuButton ||
        !menuPanel ||
        !scorecardHost ||
        !scorecardMount ||
        !backButton) {
        throw new Error('Score update shell was not created correctly.');
    }
    if (!menuButton.dataset.bound) {
        menuButton.dataset.bound = 'true';
        menuButton.addEventListener('click', () => {
            const isHidden = menuPanel.classList.toggle('hidden');
            menuButton.setAttribute('aria-expanded', String(!isHidden));
        });
    }
    return {
        root,
        summaryCard,
        scoreUpdateMessageElement,
        viewButton,
        menuButton,
        menuPanel,
        scorecardHost,
        scorecardMount,
        backButton,
    };
}
function normalizeInitial(initial) {
    return String(initial ?? '').trim().toUpperCase();
}
function getCurrentPlayerInitial(scorecard) {
    return AccessManager.fromBrowserStorage(scorecard).currentPlayerId ?? '';
}
function getPlayerStatuses(scorecard) {
    const existing = Array.isArray(scorecard.playerStatuses)
        ? scorecard.playerStatuses
        : [];
    return (scorecard.players ?? []).map((player) => {
        const memberId = String(player.memberId ?? '').trim();
        const displayCode = normalizeInitial(player.displayCode);
        const found = existing.find((status) => String(status.memberId ?? '').trim() === memberId);
        const status = PlayerStatus.from(found?.status);
        return {
            memberId,
            displayCode,
            status: status.toFirestoreValue(),
            checkedInBy: status.value === 'self'
                ? memberId
                : found?.checkedInBy ?? '',
        };
    });
}
function getScorekeeperInitial(scorecard) {
    return AccessManager.fromBrowserStorage(scorecard).scorekeeperId ?? '';
}
function getDisplayedAccessCode(scorecard) {
    return (getStoredAccessCodeForScorecard(scorecard) ||
        ViewCode.normalize(scorecard.viewCode ?? ''));
}
async function saveRoundRoleState(repo, scorecard, patch) {
    await repo.ScoreCards.update({
        ...scorecard,
        ...patch,
    });
}
async function renderRoundRoleControls(repo, scorecard) {
    const { menuPanel } = ensureUpdateShell();
    const access = AccessManager.fromBrowserStorage(scorecard);
    const currentPlayerId = access.currentPlayerId ?? '';
    const playerStatuses = getPlayerStatuses(scorecard);
    const scorekeeperId = access.scorekeeperId ?? '';
    const playerStatusOptions = [
        { value: 'none', label: 'Not checked in' },
        { value: 'self', label: 'Checked in - has phone' },
        { value: 'proxy', label: 'Checked in - no phone' },
        { value: 'withdrawn', label: 'Withdrawn' },
    ];
    const getDisplayCode = (memberId) => playerStatuses.find(p => sameId(p.memberId, memberId))?.displayCode ?? memberId;
    const sameId = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
    const currentPlayer = playerStatuses.find((player) => sameId(player.memberId, currentPlayerId));
    const scorekeeper = playerStatuses.find((player) => sameId(player.memberId, scorekeeperId));
    const versionInfo = VersionManager.getVersionInfo();
    const identityDebugHtml = `
  <div class="round-menu-muted">
    <strong>${escapeHtml(versionInfo.appName)}</strong><br>
    Version: ${escapeHtml(versionInfo.version)}<br>
    Built: ${escapeHtml(versionInfo.built)}<br>

    <button
      id="reloadAppBtn" type="button"
      style="
        position: absolute;
        top: 8px;
        right: 8px;
        font-size: 12px;
        padding: 4px 8px;
      "
    > ↻ Reload App </button>

    <button
      id="captainToolsBtn" type="button"
      style="
        position: absolute;
        top: 40px;
        right: 8px;
        font-size: 12px;
        padding: 4px 8px;
      "
    > Captain Tools </button>

    <button
      id="leaveRoundBtn" type="button"
      style="
        position: absolute;
        top: 75px;
        right: 8px;
        font-size: 12px;
        padding: 4px 8px;
      "
    > Leave Round </button>

    <button
      id="updateHdcpBtn" type="button"
      style="
        position: absolute;
        top: 110px;
        right: 8px;
        font-size: 12px;
        padding: 4px 8px;
      "
    > Update Handicap </button>

  </div>

<br>
<strong>
  Connected as: ${escapeHtml(currentPlayer?.displayCode ?? '(none)')}
</strong>

<a href="#" id="changeConnectedPlayerBtn" class="change-player-link">
  change
</a><br>

<strong>
  Authenticated as: ${escapeHtml(repo.Auth.getCurrentUserEmail() ?? 'NA')}
</strong><br>
`;
    const restoreScorekeeperSelect = () => {
        if (!scorekeeperSelect)
            return;
        const matchingOption = Array.from(scorekeeperSelect.options)
            .find(option => sameId(option.value, scorekeeperId));
        scorekeeperSelect.value = matchingOption?.value ?? '';
    };
    const scorekeeperOptions = playerStatuses
        .map((player) => {
        const selected = sameId(player.memberId, scorekeeperId) ? 'selected' : '';
        return `<option value="${escapeHtml(player.memberId)}" ${selected}>${escapeHtml(player.displayCode)}</option>`;
    })
        .join('');
    const playerStatusRows = playerStatuses
        .map((player) => {
        const selectedStatus = PlayerStatus.from(player.status).value;
        const options = playerStatusOptions
            .map((option) => {
            const selected = option.value === selectedStatus ? 'selected' : '';
            return `<option value="${escapeHtml(option.value)}" ${selected}>${escapeHtml(option.label)}</option>`;
        })
            .join('');
        return `
        <label class="round-menu-label" for="playerStatusSelect-${escapeHtml(player.displayCode)}">
          ${escapeHtml(player.displayCode)} Status
          <select class="player-status-select" id="playerStatusSelect-${escapeHtml(player.displayCode)}" data-player="${escapeHtml(player.displayCode)}">
            ${options}
          </select>
        </label>
      `;
    })
        .join('');
    const playerOnlyControls = currentPlayerId
        ? `
      <label class="round-menu-label" for="scorekeeperSelect">
        Scorekeeper
        <select id="scorekeeperSelect" ${access.canEditScorekeeper() ? '' : 'disabled'}>
          ${scorekeeperOptions}
        </select>
      </label>

      <details class="round-menu-expander">
        <summary class="round-menu-subtitle">Player Status</summary>
        ${playerStatusRows}
      </details>
    `
        : `
      <div class="round-menu-muted">Gallery viewer</div>
    `;
    menuPanel.innerHTML = `
    <div class="round-menu-title">[Round Options]</div>
${identityDebugHtml}

    <label class="round-menu-label" for="roundAccessCodeInput">
      Access Code
      <input
        id="roundAccessCodeInput"
        class="round-menu-input"
        type="text"
        value="${escapeHtml(getDisplayedAccessCode(scorecard))}"
        autocomplete="off"
        spellcheck="false"
      />
    </label>

    ${playerOnlyControls}
  `;
    // Wire up the change connected player button.
    document
        .getElementById('changeConnectedPlayerBtn')
        ?.addEventListener('click', async () => {
        const choices = playerStatuses
            .map(p => `${p.displayCode} = ${p.memberId}`)
            .join('\n');
        const selectedCode = prompt(`Change this device to which player?\n\n${choices}\n\nEnter player code:`);
        if (!selectedCode)
            return;
        const selectedPlayer = playerStatuses.find(p => p.displayCode.toUpperCase() === selectedCode.trim().toUpperCase());
        if (!selectedPlayer) {
            alert('Player not found.');
            return;
        }
        if (!confirm(`Change this device from ${currentPlayer?.displayCode ?? '(none)'} to ${selectedPlayer.displayCode}?`)) {
            return;
        }
        setConnectedPlayer(selectedPlayer.memberId);
        location.reload();
    });
    const accessCodeInput = document.getElementById('roundAccessCodeInput');
    const originalAccessCode = getDisplayedAccessCode(scorecard);
    let accessCodeSaved = false;
    let accessCodeSaveInProgress = false;
    const saveAccessCode = async () => {
        if (!accessCodeInput || accessCodeSaved || accessCodeSaveInProgress)
            return;
        const currentAccessCode = ViewCode.normalize(originalAccessCode);
        accessCodeSaveInProgress = true;
        try {
            const nextAccessCode = ViewCode.normalize(accessCodeInput.value);
            if (!nextAccessCode || nextAccessCode === currentAccessCode) {
                accessCodeInput.value = currentAccessCode;
                return;
            }
            if (!ViewCode.isValid(nextAccessCode)) {
                alert(`A scorecard was not found with access code ${nextAccessCode}.`);
                accessCodeInput.value = currentAccessCode;
                return;
            }
            const nextViewCode = ViewCode.toGalleryCode(nextAccessCode);
            const existingScorecard = await repo.ScoreCards.findActiveByViewCode(nextViewCode);
            if (!existingScorecard?.id) {
                alert(`A scorecard was not found with access code ${nextAccessCode}.`);
                accessCodeInput.value = currentAccessCode;
                return;
            }
            const roundAccess = ViewCode.validateForRound(nextAccessCode, existingScorecard.playerAccessCode ?? existingScorecard.viewCode);
            if (!roundAccess.isValidForRound) {
                alert(`A scorecard was not found with access code ${roundAccess.normalizedInput}.`);
                accessCodeInput.value = currentAccessCode;
                return;
            }
            if (existingScorecard.id !== scorecard.id) {
                alert(`A scorecard was not found with access code ${nextAccessCode}.`);
                accessCodeInput.value = currentAccessCode;
                return;
            }
            const ok = confirm(`Change access code from ${currentAccessCode} to ${nextAccessCode}?`);
            if (!ok) {
                accessCodeInput.value = currentAccessCode;
                return;
            }
            saveBrowserAccessSession(roundAccess.normalizedInput, roundAccess.accessMode);
            accessCodeInput.value = roundAccess.normalizedInput;
            accessCodeSaved = true;
            // Changing the access code changes this browser session's permissions.
            // Reloading rebuilds the scorecard with the correct enabled/disabled score inputs.
            window.location.reload();
        }
        catch (error) {
            alert(`Unable to change access code: ${error instanceof Error ? error.message : String(error)}`);
            accessCodeInput.value = currentAccessCode;
        }
        finally {
            accessCodeSaveInProgress = false;
        }
    };
    //Wire up the app refresh button.
    document
        .getElementById("reloadAppBtn")
        ?.addEventListener("click", () => {
        void VersionManager.reloadAppFresh();
    });
    //Wire up the captain tools button.
    const captainToolsBtn = document
        .getElementById("captainToolsBtn");
    captainToolsBtn?.addEventListener("click", () => {
        window.location.href = "/RoundSetup/";
    });
    if (captainToolsBtn) {
        captainToolsBtn.hidden = true;
        const captainEmail = USE_FIREBASE_EMULATORS
            ? DEV_CAPTAIN_EMAIL
            : repo.Auth.getCurrentUserEmail()?.trim().toLowerCase();
        const isCaptain = await repo.Groups.isCaptain(scorecard.group, captainEmail);
        captainToolsBtn.hidden = !isCaptain;
    }
    //Wire up the leave round button.
    const leaveRoundBtn = document
        .getElementById("leaveRoundBtn");
    if (leaveRoundBtn) {
        leaveRoundBtn.addEventListener("click", () => {
            clearStoredRoundSession();
            window.location.href = window.location.origin + window.location.pathname;
        });
    }
    function clearStoredRoundSession() {
        localStorage.removeItem(ACCESS_CODE_STORAGE_KEY);
        localStorage.removeItem(CURRENT_ACCESS_MODE_STORAGE_KEY);
    }
    //Wire up the update handicap button.
    const updateHdcpBtn = document
        .getElementById("updateHdcpBtn");
    if (updateHdcpBtn) {
        updateHdcpBtn.addEventListener("click", async () => {
            try {
                const access = AccessManager.fromBrowserStorage(scorecard);
                if (access.canEditScores()) {
                    await showScorekeeperHandicapEditor(repo, scorecard);
                }
                else {
                    await updateMyProfile(repo, scorecard);
                }
            }
            catch (error) {
                console.error("[Update Handicap] failed:", error);
                alert(error instanceof Error ? error.message : String(error));
            }
        });
    }
    accessCodeInput?.addEventListener('blur', () => void saveAccessCode());
    accessCodeInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            accessCodeInput.blur();
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            accessCodeSaved = true;
            accessCodeInput.value = originalAccessCode;
            accessCodeInput.blur();
        }
    });
    const scorekeeperSelect = document.getElementById('scorekeeperSelect');
    scorekeeperSelect?.addEventListener('change', () => {
        const nextScorekeeper = String(scorekeeperSelect.value ?? '').trim();
        const nextPlayerStatus = access.getPlayerStatus(nextScorekeeper, scorecard);
        if (nextPlayerStatus?.value === "proxy") {
            alert("Players checked in without a phone cannot be scorekeeper.");
            restoreScorekeeperSelect();
            return;
        }
        if (!access.canEditScorekeeper()) {
            restoreScorekeeperSelect();
            return;
        }
        if (!nextScorekeeper || sameId(nextScorekeeper, scorekeeperId))
            return;
        const isSelfClaim = !!access.currentPlayerId &&
            sameId(nextScorekeeper, access.currentPlayerId) &&
            !access.isScorekeeper;
        const isTransfer = access.canTransferScorekeeper();
        if (!isSelfClaim && !isTransfer) {
            alert('Only the current scorekeeper can transfer scorekeeper to another player. You may claim scorekeeper for yourself.');
            restoreScorekeeperSelect();
            return;
        }
        const message = isSelfClaim
            ? `Claim scorekeeper from ${getDisplayCode(scorekeeperId) || '(none)'}?`
            : `Change scorekeeper from ${getDisplayCode(scorekeeperId) || '(none)'} to ${getDisplayCode(nextScorekeeper)}?`;
        const ok = confirm(message);
        if (!ok) {
            restoreScorekeeperSelect();
            return;
        }
        void saveRoundRoleState(repo, scorecard, {
            scoreKeeperPlayerId: nextScorekeeper,
        });
    });
    document.querySelectorAll('.player-status-select').forEach((select) => {
        select.addEventListener('change', () => {
            const targetDisplayCode = normalizeInitial(select.dataset.player);
            const nextStatus = PlayerStatus.from(select.value);
            const targetPlayer = playerStatuses.find((player) => player.displayCode === targetDisplayCode);
            if (!currentPlayerId || !targetPlayer) {
                select.value = PlayerStatus.from(targetPlayer?.status).value;
                return;
            }
            const currentStatus = PlayerStatus.from(targetPlayer?.status);
            if (nextStatus.value === currentStatus.value)
                return;
            if (nextStatus.value === 'self' && !sameId(targetPlayer.memberId, currentPlayerId)) {
                alert('You can only mark yourself as checked in with phone. Use Checked in - no phone for another player.');
                select.value = currentStatus.value;
                return;
            }
            if (nextStatus.value === 'proxy' && sameId(targetPlayer.memberId, currentPlayerId)) {
                alert('Use Checked in - has phone for yourself. Checked in - no phone is for another player.');
                select.value = currentStatus.value;
                return;
            }
            const message = `Change ${targetDisplayCode} from ${currentStatus.getDisplayText()} to ${nextStatus.getDisplayText()}?`;
            const ok = confirm(message);
            if (!ok) {
                select.value = currentStatus.value;
                return;
            }
            const updatedStatuses = playerStatuses.map((player) => {
                if (player.displayCode !== targetDisplayCode)
                    return player;
                return {
                    memberId: player.memberId,
                    displayCode: player.displayCode,
                    status: nextStatus.toFirestoreValue(),
                    checkedInBy: nextStatus.value === 'self'
                        ? player.memberId
                        : nextStatus.value === 'proxy'
                            ? currentPlayerId
                            : '',
                };
            });
            void saveRoundRoleState(repo, scorecard, {
                playerStatuses: updatedStatuses,
            });
        });
    });
}
function setScorecardVisible(isVisible) {
    const { root, summaryCard, scorecardHost, menuPanel, menuButton } = ensureUpdateShell();
    if (isVisible) {
        root.classList.add('scorecard-mode');
        summaryCard.classList.add('hidden');
        scorecardHost.classList.remove('hidden');
        menuPanel.classList.add('hidden');
        menuButton.setAttribute('aria-expanded', 'false');
    }
    else {
        root.classList.remove('scorecard-mode');
        summaryCard.classList.remove('hidden');
        scorecardHost.classList.add('hidden');
    }
}
function escapeHtml(value) {
    return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}
function getCurrentPlayerId() {
    const value = String(localStorage.getItem(CURRENT_PLAYER_STORAGE_KEY) ?? "").trim();
    return value || null;
}
async function updateMyProfile(repo, scorecard, targetPlayerId, newHdcpOverride, updateScorecard = true) {
    const currentPlayerId = getCurrentPlayerId();
    const playerIdToUpdate = String(targetPlayerId ?? currentPlayerId ?? "").trim();
    if (!playerIdToUpdate) {
        alert("No player is connected.");
        return;
    }
    const sameId = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
    const scorecardPlayer = scorecard.players?.find((p) => sameId(p.memberId, playerIdToUpdate));
    if (!scorecardPlayer) {
        alert("The selected player was not found on this scorecard.");
        return;
    }
    const group = await repo.Groups.get(scorecard.group);
    const member = group?.members?.find((m) => sameId(m.id, playerIdToUpdate));
    if (!member) {
        alert("The selected player was not found in the group member list.");
        return;
    }
    const currentHdcp = Number(scorecardPlayer.handicap ?? member.hdcp ?? 0);
    let newHdcp;
    if (newHdcpOverride === undefined) {
        const input = prompt("Enter your handicap:", String(currentHdcp));
        if (input === null)
            return;
        newHdcp = Number(input);
    }
    else {
        newHdcp = newHdcpOverride;
    }
    if (!Number.isFinite(newHdcp)) {
        alert("Enter a valid handicap.");
        return;
    }
    await repo.Groups.updateMemberProfileByNickName(member.nickName, {
        name: member.name ?? "",
        nickName: member.nickName ?? "",
        initial: member.initial ?? "",
        hdcp: newHdcp,
        email: member.email ?? "",
        phone: member.phone ?? "",
    });
    const updatedScorecard = {
        ...scorecard,
        players: scorecard.players.map((p) => {
            if (!sameId(p.memberId, playerIdToUpdate))
                return p;
            return {
                ...p,
                handicap: newHdcp,
            };
        }),
    };
    if (updateScorecard) {
        const updatedScorecard = {
            ...scorecard,
            players: scorecard.players.map((p) => {
                if (!sameId(p.memberId, playerIdToUpdate))
                    return p;
                return {
                    ...p,
                    handicap: newHdcp,
                };
            }),
        };
        await repo.ScoreCards.update(updatedScorecard);
    }
}
async function showScorekeeperHandicapEditor(repo, scorecard) {
    const group = await repo.Groups.get(scorecard.group);
    if (!group) {
        alert("Group not found.");
        return;
    }
    const rows = scorecard.players.map((player) => {
        const member = group.members?.find((m) => String(m.id ?? "").trim().toLowerCase() ===
            String(player.memberId ?? "").trim().toLowerCase());
        const name = member?.name ?? player.displayCode ?? "";
        const currentHdcp = Number(player.handicap ?? member?.hdcp ?? 0);
        return {
            memberId: String(player.memberId ?? "").trim(),
            displayCode: String(player.displayCode ?? "").trim(),
            name,
            currentHdcp,
        };
    });
    const lines = rows
        .map((r, index) => `${index + 1}. ${r.displayCode} ${r.name}: ${r.currentHdcp}`)
        .join("\n");
    const selected = prompt(`Update which player handicap?\n\n${lines}\n\nEnter player number, or A for all:`);
    if (!selected)
        return;
    const trimmed = selected.trim().toUpperCase();
    if (trimmed === "A") {
        const handicapChanges = new Map();
        for (const row of rows) {
            const input = prompt(`Enter handicap for ${row.displayCode} ${row.name}:`, String(row.currentHdcp));
            if (input === null)
                continue;
            const newHdcp = Number(input);
            if (!Number.isFinite(newHdcp)) {
                alert(`Invalid handicap for ${row.displayCode}. Skipped.`);
                continue;
            }
            if (newHdcp !== row.currentHdcp) {
                handicapChanges.set(row.memberId.toLowerCase(), newHdcp);
            }
        }
        if (handicapChanges.size === 0) {
            alert("No handicap changes were made.");
            return;
        }
        // Update member records
        for (const row of rows) {
            const newHdcp = handicapChanges.get(row.memberId.toLowerCase());
            if (newHdcp === undefined)
                continue;
            await updateMyProfile(repo, scorecard, row.memberId, newHdcp, false);
        }
        // Save the scorecard once with all handicap changes applied
        const updatedScorecard = {
            ...scorecard,
            players: scorecard.players.map((p) => {
                const key = String(p.memberId ?? "").trim().toLowerCase();
                const newHdcp = handicapChanges.get(key);
                if (newHdcp === undefined)
                    return p;
                return {
                    ...p,
                    handicap: newHdcp,
                };
            }),
        };
        await repo.ScoreCards.update(updatedScorecard);
        alert("Handicap updates complete.");
        window.location.reload();
        return;
    }
    const selectedIndex = Number(trimmed) - 1;
    const row = rows[selectedIndex];
    if (!row) {
        alert("Player not found.");
        return;
    }
    const input = prompt(`Enter handicap for ${row.displayCode} ${row.name}:`, String(row.currentHdcp));
    if (input === null)
        return;
    const newHdcp = Number(input);
    if (!Number.isFinite(newHdcp)) {
        alert("Enter a valid handicap.");
        return;
    }
    await updateMyProfile(repo, scorecard, row.memberId, newHdcp);
    alert("Handicap updated.");
    window.location.reload();
}
//# sourceMappingURL=ShowScorecard.js.map